/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import {
    Box,
    Stack,
    Typography,
    Button,
    IconButton,
    Chip,
    TextField,
    Switch,
    Divider,
    CircularProgress,
    Collapse,
    Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Iconify } from '../../../components';
import { callApi } from '../../../utils/apiCall';
import useSnackbar from '../../../hooks/useCustomSnackbar';
import useDirtyForm from '../../../hooks/useDirtyForm';
import { getUpcomingBankHolidays } from '../../../constants/ukBankHolidays';
import StickySaveBar from '../../../components/settings/StickySaveBar';

// ─── closure form ─────────────────────────────────────────────────────────────

const EMPTY_FORM = {
    start_date: null,
    end_date: null,
    reason: '',
    recurring: false,
};

function ClosureForm({ initial, onSave, onCancel, saving }) {
    const [form, setForm] = useState({ ...EMPTY_FORM, ...initial });

    const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

    return (
        <Box
            sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                p: 2,
                bgcolor: '#f8fafc',
            }}
        >
            <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <DatePicker
                        label="Start date"
                        value={form.start_date ? new Date(form.start_date) : null}
                        onChange={(val) => set('start_date', val ? dayjs(val).format('YYYY-MM-DD') : null)}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                    <DatePicker
                        label="End date"
                        value={form.end_date ? new Date(form.end_date) : null}
                        onChange={(val) => set('end_date', val ? dayjs(val).format('YYYY-MM-DD') : null)}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                </Stack>
                <TextField
                    label="Reason"
                    size="small"
                    value={form.reason}
                    onChange={(e) => set('reason', e.target.value)}
                    fullWidth
                    placeholder="e.g. Christmas closure, Staff training day"
                />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body2">Recurring annually</Typography>
                    <Switch
                        checked={form.recurring}
                        onChange={(e) => set('recurring', e.target.checked)}
                        size="small"
                    />
                </Stack>
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onCancel}
                        disabled={saving}
                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => onSave(form)}
                        disabled={saving || !form.start_date || !form.end_date}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            bgcolor: '#00A76F',
                            '&:hover': { bgcolor: '#007B55' },
                        }}
                    >
                        Save
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

ClosureForm.propTypes = {
    initial: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    saving: PropTypes.bool,
};
ClosureForm.defaultProps = { initial: {}, saving: false };

// ─── closure row ──────────────────────────────────────────────────────────────

function ClosureRow({ closure, onEdit, onDelete, deleting, isBankHoliday, isWorking, onToggleWorking }) {
    const start = dayjs(closure.start_date);
    const end = closure.end_date ? dayjs(closure.end_date) : start;
    const isSameDay = start.isSame(end, 'day');
    const dateLabel = isSameDay
        ? start.format('D MMM YYYY')
        : `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;

    const isPast = end.isBefore(dayjs(), 'day');

    // Pre-compute colours to avoid nested ternaries
    const defaultBorder = '#e2e8f0';
    const holidayBorder = isWorking ? alpha('#22c55e', 0.3) : alpha('#f59e0b', 0.2);
    const borderColor = isBankHoliday ? holidayBorder : defaultBorder;

    const holidayBg = isWorking ? alpha('#22c55e', 0.03) : alpha('#f59e0b', 0.03);
    const bgColor = isBankHoliday ? holidayBg : '#fff';

    const holidayIconColor = isWorking ? '#22c55e' : '#f59e0b';
    const iconColor = isBankHoliday ? holidayIconColor : '#06b6d4';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.25,
                px: 1.5,
                borderRadius: '10px',
                border: `1px solid ${borderColor}`,
                bgcolor: bgColor,
                opacity: isPast ? 0.5 : 1,
            }}
        >
            <Iconify
                icon={isBankHoliday ? 'mdi:flag-outline' : 'solar:calendar-mark-bold-duotone'}
                width={20}
                sx={{ color: iconColor, flexShrink: 0 }}
            />
            <Box flex={1} minWidth={0}>
                <Typography variant="body2" fontWeight={600} noWrap sx={{ textDecoration: isWorking ? 'line-through' : 'none', color: isWorking ? '#94a3b8' : 'text.primary' }}>
                    {closure.reason || closure.name || 'Closure'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {dateLabel}
                </Typography>
            </Box>
            {isBankHoliday && !isWorking && (
                <Chip label="Closed" size="small" sx={{ fontSize: 11, height: 22, bgcolor: alpha('#f59e0b', 0.1), color: '#b45309' }} />
            )}
            {isBankHoliday && isWorking && (
                <Chip label="Open" size="small" sx={{ fontSize: 11, height: 22, bgcolor: alpha('#22c55e', 0.1), color: '#16a34a' }} />
            )}
            {isBankHoliday && onToggleWorking && !isPast && (
                <Switch
                    size="small"
                    checked={isWorking}
                    onChange={() => onToggleWorking(closure.start_date || closure.date)}
                    sx={{ ml: 0.5 }}
                />
            )}
            {closure.recurring && !isBankHoliday && (
                <Chip label="Recurring" size="small" sx={{ fontSize: 11, height: 22, bgcolor: alpha('#06b6d4', 0.08), color: '#0891b2' }} />
            )}
            {isPast && (
                <Chip label="Past" size="small" sx={{ fontSize: 11, height: 22, bgcolor: '#f1f5f9', color: '#94a3b8' }} />
            )}
            {!isBankHoliday && onEdit && (
                <IconButton size="small" onClick={() => onEdit(closure)} sx={{ color: '#64748b' }}>
                    <Iconify icon="solar:pen-2-bold" width={16} />
                </IconButton>
            )}
            {!isBankHoliday && onDelete && (
                <IconButton size="small" onClick={() => onDelete(closure.id)} disabled={deleting} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                    {deleting ? <CircularProgress size={14} /> : <Iconify icon="solar:trash-bin-trash-bold" width={16} />}
                </IconButton>
            )}
        </Box>
    );
}

ClosureRow.propTypes = {
    closure: PropTypes.object.isRequired,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    deleting: PropTypes.bool,
    isBankHoliday: PropTypes.bool,
    isWorking: PropTypes.bool,
    onToggleWorking: PropTypes.func,
};
ClosureRow.defaultProps = { onEdit: null, onDelete: null, deleting: false, isBankHoliday: false, isWorking: false, onToggleWorking: null };

// ─── main component ────────────────────────────────────────────────────────────

export default function ClosuresTab({ practiceId, canEdit }) {
    const [backendAvailable, setBackendAvailable] = useState(null); // null=checking, true, false
    const [closures, setClosures] = useState([]);
    const [workingHolidays, setWorkingHolidays] = useState({}); // { '2026-04-03': true } = open on that bank holiday
    const [showBankHolidays, setShowBankHolidays] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingClosure, setEditingClosure] = useState(null);
    const [formSaving, setFormSaving] = useState(false);
    const [holidaysSaving, setHolidaysSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const { isDirty: isHolidaysDirty, markDirty: markHolidaysDirty, markClean: markHolidaysClean } = useDirtyForm({});

    const STORAGE_KEY = `working_holidays_${practiceId}`;
    const endpoint = `/api/v1/practices/${practiceId}/closures/`;
    const bankHolidays = getUpcomingBankHolidays();

    // Restore workingHolidays from localStorage on mount
    useEffect(() => {
        if (!practiceId) return;
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) setWorkingHolidays(JSON.parse(saved));
        } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId]);

    const load = useCallback(async () => {
        if (!practiceId) return;
        try {
            const resp = await callApi(endpoint);
            if (!resp.ok) {
                setBackendAvailable(false);
                return;
            }
            const list = Array.isArray(resp.data) ? resp.data : resp.data?.results || [];
            const sorted = [...list].sort((a, b) => a.start_date.localeCompare(b.start_date));
            setClosures(sorted);
            setBackendAvailable(true);
        } catch {
            setBackendAvailable(false);
        }
    }, [practiceId]);

    useEffect(() => { load(); }, [load]);

    const handleAddSave = async (form) => {
        if (!backendAvailable) {
            // Store locally only
            setClosures((prev) => [...prev, { ...form, id: `local-${Date.now()}`, _local: true }]
                .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || '')));
            setShowAddForm(false);
            enqueueSnackbar('Closure added (saved locally — will sync when backend is ready)', { variant: 'info' });
            return;
        }
        setFormSaving(true);
        try {
            const resp = await callApi(endpoint, 'POST', form);
            if (!resp.ok) throw new Error(resp.error || 'Failed to add closure');
            enqueueSnackbar('Closure added', { variant: 'success' });
            setShowAddForm(false);
            load();
        } catch (err) {
            enqueueSnackbar(err?.message || 'Failed to add closure', { variant: 'error' });
        } finally {
            setFormSaving(false);
        }
    };

    const handleEditSave = async (form) => {
        setFormSaving(true);
        try {
            const resp = await callApi(`${endpoint}${editingClosure.id}/`, 'PATCH', form);
            if (!resp.ok) throw new Error(resp.error || 'Failed to update closure');
            enqueueSnackbar('Closure updated', { variant: 'success' });
            setEditingClosure(null);
            load();
        } catch (err) {
            enqueueSnackbar(err?.message || 'Failed to update closure', { variant: 'error' });
        } finally {
            setFormSaving(false);
        }
    };

    const handleDelete = async (id) => {
        // Local-only closure
        if (String(id).startsWith('local-')) {
            setClosures((prev) => prev.filter((c) => c.id !== id));
            return;
        }
        setDeletingId(id);
        try {
            const resp = await callApi(`${endpoint}${id}/`, 'DELETE');
            if (!resp.ok) throw new Error(resp.error || 'Failed to delete closure');
            enqueueSnackbar('Closure removed', { variant: 'success' });
            setClosures((prev) => prev.filter((c) => c.id !== id));
        } catch (err) {
            enqueueSnackbar(err?.message || 'Failed to delete closure', { variant: 'error' });
        } finally {
            setDeletingId(null);
        }
    };

    const toggleWorkingHoliday = (date) => {
        setWorkingHolidays((prev) => ({ ...prev, [date]: !prev[date] }));
        markHolidaysDirty();
    };

    const saveWorkingHolidays = () => {
        setHolidaysSaving(true);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(workingHolidays));
            enqueueSnackbar('Bank holiday settings saved', { variant: 'success' });
            markHolidaysClean();
        } catch {
            enqueueSnackbar('Failed to save bank holiday settings', { variant: 'error' });
        } finally {
            setHolidaysSaving(false);
        }
    };

    const discardWorkingHolidays = () => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            setWorkingHolidays(saved ? JSON.parse(saved) : {});
        } catch { /* ignore */ }
        markHolidaysClean();
    };

    const closedHolidayCount = bankHolidays.filter((h) => !workingHolidays[h.date]).length;

    // ── loading state ──
    if (backendAvailable === null) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    return (
        <Box>
            {/* Backend status info */}
            {!backendAvailable && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }} icon={<Iconify icon="mdi:information-outline" width={20} />}>
                    Closures backend is not yet deployed. You can still add closures locally and view UK bank holidays. They will sync automatically once the backend is ready.
                </Alert>
            )}

            {/* ── Bank Holidays section ── */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon="mdi:flag-outline" width={18} sx={{ color: '#f59e0b' }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                        UK Bank Holidays
                    </Typography>
                    <Chip label={`${closedHolidayCount} closed · ${bankHolidays.length - closedHolidayCount} open`} size="small" sx={{ fontSize: 11, height: 20, bgcolor: alpha('#f59e0b', 0.1), color: '#b45309' }} />
                </Stack>
                <Button
                    size="small"
                    onClick={() => setShowBankHolidays(!showBankHolidays)}
                    sx={{ textTransform: 'none', fontSize: 12, color: '#64748b' }}
                >
                    {showBankHolidays ? 'Hide' : 'Show'}
                </Button>
            </Stack>

            <Collapse in={showBankHolidays}>
                <Stack spacing={0.75} mb={2}>
                    {bankHolidays.map((h) => (
                        <ClosureRow
                            key={h.date}
                            closure={{ start_date: h.date, end_date: h.date, name: h.name }}
                            isBankHoliday
                            isWorking={Boolean(workingHolidays[h.date])}
                            onToggleWorking={canEdit ? toggleWorkingHoliday : null}
                        />
                    ))}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Toggle the switch to mark your practice as open on a bank holiday.
                    </Typography>
                </Stack>
            </Collapse>

            {canEdit && <StickySaveBar isDirty={isHolidaysDirty} onSave={saveWorkingHolidays} onDiscard={discardWorkingHolidays} saving={holidaysSaving} />}

            <Divider sx={{ mb: 2 }} />

            {/* ── Custom Closures section ── */}
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Iconify icon="solar:calendar-mark-bold-duotone" width={18} sx={{ color: '#06b6d4' }} />
                <Typography variant="subtitle2" fontWeight={700}>
                    Custom Closures
                </Typography>
                {closures.length > 0 && (
                    <Chip label={closures.length} size="small" sx={{ fontSize: 11, height: 20, bgcolor: alpha('#06b6d4', 0.1), color: '#0891b2' }} />
                )}
            </Stack>

            <Stack spacing={1} mb={2}>
                {closures.length === 0 && !showAddForm && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No custom closures set. Add one below.
                    </Typography>
                )}
                {closures.map((closure) =>
                    editingClosure?.id === closure.id ? (
                        <ClosureForm
                            key={closure.id}
                            initial={editingClosure}
                            onSave={handleEditSave}
                            onCancel={() => setEditingClosure(null)}
                            saving={formSaving}
                        />
                    ) : (
                        <ClosureRow
                            key={closure.id}
                            closure={closure}
                            onEdit={canEdit && backendAvailable ? setEditingClosure : null}
                            onDelete={canEdit ? handleDelete : null}
                            deleting={deletingId === closure.id}
                        />
                    )
                )}
            </Stack>

            {/* Add form */}
            <Collapse in={canEdit && showAddForm}>
                <Box mb={2}>
                    <ClosureForm
                        onSave={handleAddSave}
                        onCancel={() => setShowAddForm(false)}
                        saving={formSaving}
                    />
                </Box>
            </Collapse>

            {canEdit && !showAddForm && (
                <Button
                    startIcon={<Iconify icon="solar:add-circle-bold" width={18} />}
                    onClick={() => setShowAddForm(true)}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#00A76F',
                        '&:hover': { bgcolor: alpha('#00A76F', 0.06) },
                    }}
                >
                    Add Closure
                </Button>
            )}
        </Box>
    );
}

ClosuresTab.propTypes = {
    practiceId: PropTypes.any,
    canEdit: PropTypes.bool,
};

ClosuresTab.defaultProps = {
    practiceId: null,
    canEdit: false,
};
